import React, {useEffect, useRef, useState,} from "react";
import {Icon, notification, Popover,} from "antd";
import {Link} from "react-router-dom";

import {switchUserWorkMode,} from "../../application/user-profile/switch-user-work-mode";
import {SWITCH_TO_DIFFERENT_COMPANY_TYPE,} from "../../helpers/user_permission.helper";
import i18n from "../../i18n";
import {userServices} from "../../services";
import {buildProfileCardViewModel,} from "../../presentation/user-profile/build-profile-card-view-model";
import ProfileCard from "./ProfileCard";
import {useProfileCabinetOnboarding,} from "../../presentation/user-profile/use-profile-cabinet-onboarding";
import ProfileCabinetOnboarding from "./ProfileCabinetOnboarding";

export default function ProfileBlock({
                                         user,
                                         isDarkTheme,
                                         onLogout,
                                         onUserTypeChanged,
                                     }) {
    const [isSwitching, setIsSwitching] = useState(false);
    const [isProfileOpen, setProfileOpen] = useState(false);
    const isGuestUser = !user || user.isGuest();

    const {
        isVisible: isCabinetOnboardingVisible,
        dismiss: dismissCabinetOnboarding,
    } = useProfileCabinetOnboarding(
        isGuestUser
            ? null
            : user.info.id
    );

    const isSwitchingRef = useRef(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    if (isGuestUser) {
        return (
            <Link
                to="/login"
                className="header__login-link head-login__link-login"
            >
                {i18n.t("main.header.login")}
            </Link>
        );
    }

    const profile = buildProfileCardViewModel({
        userInfo: user.info,
        translate: (key) => i18n.t(key),
    });

    const canSwitchUserType = Boolean(
        user.isAccessTo(
            SWITCH_TO_DIFFERENT_COMPANY_TYPE
        ) &&
        user.info.rules &&
        user.info.rules.IsCanSwitchType
    );

    const releaseSwitching = () => {
        isSwitchingRef.current = false;

        if (isMountedRef.current) {
            setIsSwitching(false);
        }
    };

    const handleSwitchUserType = async (
        targetIsStaff
    ) => {
        if (isSwitchingRef.current) {
            return;
        }

        isSwitchingRef.current = true;
        setIsSwitching(true);

        try {
            const changed = await switchUserWorkMode({
                currentIsStaff: user.info.isStaff,
                targetIsStaff,
                switchType: userServices.switchType,
            });

            if (!changed) {
                releaseSwitching();
                return;
            }

            await onUserTypeChanged();
        } catch (error) {
            releaseSwitching();

            notification.error({
                message: i18n.t(
                    "main.header.profile.switchError"
                ),
                description: typeof error === "string"
                    ? error
                    : error && error.message,
            });
        }
    };

    const handleProfileVisibleChange = (visible) => {
        if (isCabinetOnboardingVisible) {
            return;
        }

        setProfileOpen(visible);
    };

    const handleCabinetOnboardingDismiss = () => {
        dismissCabinetOnboarding();
        setProfileOpen(false);
    };

    const handleCabinetNavigate = () => {
        dismissCabinetOnboarding();
        setProfileOpen(false);
    };

    return (
        <>
            <Popover
                content={
                    <ProfileCard
                        profile={profile}
                        canSwitchUserType={
                            canSwitchUserType
                        }
                        onSwitchUserType={
                            handleSwitchUserType
                        }
                        onLogout={onLogout}
                        onCabinetNavigate={
                            handleCabinetNavigate
                        }
                        cabinetOnboarding={
                            isCabinetOnboardingVisible
                                ? {
                                    title: i18n.t(
                                        "main.header.profile.onboarding.title"
                                    ),
                                    description: i18n.t(
                                        "main.header.profile.onboarding.description"
                                    ),
                                    mobileDescription: i18n.t(
                                        "main.header.profile.onboarding.mobileDescription"
                                    ),
                                    confirm: i18n.t(
                                        "main.header.profile.onboarding.confirm"
                                    ),
                                }
                                : null
                        }
                        onCabinetOnboardingDismiss={
                            handleCabinetOnboardingDismiss
                        }
                        isSwitching={isSwitching}
                    />
                }
                overlayClassName={`profile-popover profile-popover--modern${
                    isDarkTheme
                        ? " profile-popover--dark"
                        : ""
                }${
                    isCabinetOnboardingVisible
                        ? " profile-popover--onboarding"
                        : ""
                }`}
                placement="bottomRight"
                trigger={["hover", "click"]}
                visible={
                    isCabinetOnboardingVisible ||
                    isProfileOpen
                }
                onVisibleChange={
                    handleProfileVisibleChange
                }
                mouseEnterDelay={0.15}
                mouseLeaveDelay={0.2}
            >
                <div
                    className="head-login__profile"
                    tabIndex={0}
                    aria-label={profile.labels.openProfile}
                >
                    <span className="head-login__avatar">
                        {profile.initials}
                    </span>

                    <span className="head-login__profile-copy">
                        <Link
                            className="head-login__fio"
                            to={profile.cabinetPath}
                            title={profile.name}
                            onClick={(event) => {
                                event.stopPropagation();
                                handleCabinetNavigate();
                            }}
                        >
                            {profile.name}
                        </Link>

                        <span
                            className="head-login__company"
                            title={profile.organizationName}
                        >
                            {profile.organizationName}
                        </span>
                    </span>

                    <Icon
                        type="down"
                        className="head-login__profile-arrow"
                    />
                </div>
            </Popover>

            <ProfileCabinetOnboarding
                visible={isCabinetOnboardingVisible}
                onDismiss={
                    handleCabinetOnboardingDismiss
                }
                dismissAriaLabel={i18n.t(
                    "main.header.profile.onboarding.dismissAria"
                )}
            />
        </>
    );
}
